import React, { useEffect, useRef, useState, useCallback } from 'react';
import { cn } from '@/lib/utils';

interface StickyTableWrapperProps {
  children: React.ReactNode;
  className?: string;
}

const StickyTableWrapper: React.FC<StickyTableWrapperProps> = ({ children, className }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const floatingHeaderRef = useRef<HTMLTableElement>(null);
  const [showFloatingHeader, setShowFloatingHeader] = useState(false);
  const [floatingHeaderPos, setFloatingHeaderPos] = useState({ left: 0, width: 0 });

  const syncColumnWidths = useCallback(() => {
    if (!tableRef.current || !floatingHeaderRef.current) return;

    const originalCells = tableRef.current.querySelectorAll('thead th');
    const floatingCells = floatingHeaderRef.current.querySelectorAll('thead th');

    if (originalCells.length === floatingCells.length) {
      originalCells.forEach((cell, index) => {
        const width = cell.getBoundingClientRect().width;
        (floatingCells[index] as HTMLElement).style.width = `${width}px`;
        (floatingCells[index] as HTMLElement).style.minWidth = `${width}px`;
        (floatingCells[index] as HTMLElement).style.maxWidth = `${width}px`;
      });
    }
  }, []);

  const updateFloatingHeader = useCallback((forceReclone = false) => {
    if (!containerRef.current || !tableRef.current) return;

    const thead = tableRef.current.querySelector('thead');
    if (!thead) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const theadRect = thead.getBoundingClientRect();
    
    // Show floating header when original header would go above viewport + header offset
    const headerOffset = window.innerWidth < 768 ? 52 : 73;
    const shouldShow = theadRect.top < headerOffset && containerRect.bottom > headerOffset;
    
    setShowFloatingHeader(shouldShow);
    
    // Update position state - use scrollWidth for the table width
    setFloatingHeaderPos({
      left: containerRect.left,
      width: Math.min(containerRect.width, tableRef.current.scrollWidth)
    });

    // Update floating header position and content
    if (shouldShow && floatingHeaderRef.current) {
      const scrollLeft = containerRef.current.scrollLeft;
      floatingHeaderRef.current.style.transform = `translateX(-${scrollLeft}px)`;
      
      // Clone the header if not already done or if forced
      if (!floatingHeaderRef.current.querySelector('thead') || forceReclone) {
        const clonedThead = thead.cloneNode(true) as HTMLElement;
        floatingHeaderRef.current.innerHTML = '';
        floatingHeaderRef.current.appendChild(clonedThead);
        
        // Sync column widths after cloning
        setTimeout(() => {
          syncColumnWidths();
        }, 10);
      } else {
        // Always sync column widths when showing
        syncColumnWidths();
      }
    }
  }, [syncColumnWidths]);

  useEffect(() => {
    const handleScroll = () => {
      updateFloatingHeader();
    };

    const container = containerRef.current;
    
    // Listen to both window scroll and container scroll
    window.addEventListener('scroll', handleScroll, { passive: true });
    container?.addEventListener('scroll', handleScroll, { passive: true });
    
    // Initial check
    updateFloatingHeader();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      container?.removeEventListener('scroll', handleScroll);
    };
  }, [updateFloatingHeader]);

  // Force update when children change (e.g., when switching clinics)
  useEffect(() => {
    // Use a small timeout to ensure the DOM has been updated
    const timeoutId = setTimeout(() => {
      updateFloatingHeader(true); // Force reclone
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [children, updateFloatingHeader]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      updateFloatingHeader();
    };

    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [updateFloatingHeader]);

  // Use ResizeObserver to watch for table changes
  useEffect(() => {
    if (!tableRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      updateFloatingHeader();
    });

    resizeObserver.observe(tableRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [updateFloatingHeader]);

  return (
    <div className={cn("relative", className)}>
      {/* Floating header - positioned relative to container */}
      {showFloatingHeader && (
        <div 
          className="absolute top-0 left-0 right-0 z-50 pointer-events-none"
          style={{
            position: 'fixed',
            top: window.innerWidth < 768 ? '52px' : '73px',
            left: floatingHeaderPos.left,
            width: floatingHeaderPos.width,
          }}
        >
          <div className="overflow-hidden bg-background shadow-md pointer-events-auto">
            <table ref={floatingHeaderRef} className="w-full caption-bottom text-sm" style={{ tableLayout: 'auto', borderCollapse: 'collapse', borderSpacing: 0 }} />
          </div>
        </div>
      )}
      
      {/* Main table wrapper */}
      <div 
        ref={containerRef}
        className="schedule-table-wrapper"
      >
        <table ref={tableRef} className="w-full caption-bottom text-sm" style={{ tableLayout: 'auto', borderCollapse: 'collapse', borderSpacing: 0 }}>
          {children}
        </table>
      </div>
    </div>
  );
};

export default StickyTableWrapper;